import { useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useStore } from '../contexts/StoreContext';
import dashboardService from '../services/dashboard.service';

/**
 * Prefetch Hook
 * Provides utilities for prefetching routes and data to improve navigation performance
 * 
 * Features:
 * - Prefetch React lazy-loaded components on link hover
 * - Prefetch critical API data (e.g., Dashboard data)
 * - Debounced prefetching to avoid network overload
 * - Only prefetches for authenticated users
 */

// Cache for prefetched modules (prevents duplicate prefetching)
const prefetchedModules = new Set<string>();

// Cache for prefetched data (prevents duplicate API calls)
const prefetchedData = new Set<string>();

// Debounce delay (ms) - wait before prefetching to avoid prefetching on accidental hovers
const PREFETCH_DELAY = 100;
// Delay before prefetching dashboard data so it does not compete with initial orders load
const PREFETCH_DATA_DELAY = 2000;

export const usePrefetch = () => {
  const { isAuthenticated } = useAuth();
  const { currentStore } = useStore();
  const prefetchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const prefetchDataTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  /**
   * Prefetch a React lazy-loaded component
   * @param importFn - The import function (e.g., () => import('./pages/Dashboard/DashboardNew'))
   * @param routeKey - Unique key for the route (used to track prefetch status)
   */
  const prefetchRoute = useCallback((importFn: () => Promise<any>, routeKey: string) => {
    // Only prefetch if authenticated
    if (!isAuthenticated) return;

    // Don't prefetch if already prefetched
    if (prefetchedModules.has(routeKey)) return;

    // Clear existing timeout
    if (prefetchTimeoutRef.current) {
      clearTimeout(prefetchTimeoutRef.current);
    }

    // Debounce prefetch
    prefetchTimeoutRef.current = setTimeout(() => {
      try {
        importFn().then(() => {
          prefetchedModules.add(routeKey);
          console.log(`[PREFETCH] Route prefetched: ${routeKey}`);
        }).catch((err) => {
          console.error(`[PREFETCH] Failed to prefetch route ${routeKey}:`, err);
        });
      } catch (err) {
        console.error(`[PREFETCH] Error prefetching route ${routeKey}:`, err);
      }
    }, PREFETCH_DELAY);
  }, [isAuthenticated]);

  /**
   * Prefetch dashboard data
   * Only prefetches if user is authenticated, has a store, and isn't already on dashboard
   * @param currentPath - Current route path (to avoid prefetching if already on dashboard)
   */
  const prefetchDashboardData = useCallback(async (currentPath: string) => {
    // Only prefetch if authenticated and not already on dashboard
    if (!isAuthenticated || currentPath === '/dashboard' || !currentStore) return;

    const cacheKey = `dashboard-data-${currentStore.id}`;
    
    // Don't prefetch if already prefetched recently
    if (prefetchedData.has(cacheKey)) return;

    if (prefetchDataTimeoutRef.current) {
      clearTimeout(prefetchDataTimeoutRef.current);
    }

    prefetchDataTimeoutRef.current = setTimeout(() => {
      if (!isAuthenticated || currentPath === '/dashboard' || !currentStore) return;
      if (prefetchedData.has(cacheKey)) return;
      try {
        // Prefetch dashboard data (non-blocking)
        dashboardService.getDashboardData(currentStore.id)
          .then(() => {
            prefetchedData.add(cacheKey);
            console.log(`[PREFETCH] Dashboard data prefetched for store ${currentStore.id}`);
            
            // Clear from cache after 5 minutes to allow re-prefetching if needed
            setTimeout(() => {
              prefetchedData.delete(cacheKey);
            }, 5 * 60 * 1000);
          })
          .catch((err) => {
            console.error('[PREFETCH] Failed to prefetch dashboard data:', err);
          });
      } catch (err) {
        console.error('[PREFETCH] Error prefetching dashboard data:', err);
      }
    }, PREFETCH_DATA_DELAY);
  }, [isAuthenticated, currentStore]);

  /**
   * Clear prefetch timeout (useful for cleanup)
   */
  const clearPrefetchTimeout = useCallback(() => {
    if (prefetchTimeoutRef.current) {
      clearTimeout(prefetchTimeoutRef.current);
      prefetchTimeoutRef.current = null;
    }
    if (prefetchDataTimeoutRef.current) {
      clearTimeout(prefetchDataTimeoutRef.current);
      prefetchDataTimeoutRef.current = null;
    }
  }, []);

  return {
    prefetchRoute,
    prefetchDashboardData,
    clearPrefetchTimeout
  };
};



